import React, { useEffect, useRef, useState } from "react";

const BASE_WIDTH = 400;
const BASE_HEIGHT = 150;

export default function SignaturePad({ onSave, onClear }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);
  const sizeRef = useRef({ width: BASE_WIDTH, height: BASE_HEIGHT });
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const resizeCanvas = () => {
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const displayWidth = Math.max(260, Math.round(rect.width || BASE_WIDTH));
      const displayHeight = Math.max(150, Math.round((displayWidth / BASE_WIDTH) * BASE_HEIGHT));

      const snapshot = canvas.toDataURL("image/png");
      const image = new Image();

      canvas.width = Math.round(displayWidth * ratio);
      canvas.height = Math.round(displayHeight * ratio);
      canvas.style.height = `${displayHeight}px`;
      sizeRef.current = { width: displayWidth, height: displayHeight };

      const context = canvas.getContext("2d");
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, displayWidth, displayHeight);
      context.lineWidth = 2;
      context.lineCap = "round";
      context.lineJoin = "round";
      context.strokeStyle = "#172033";

      image.onload = () => {
        context.drawImage(image, 0, 0, displayWidth, displayHeight);
      };

      if (snapshot !== "data:,") {
        image.src = snapshot;
      }
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, []);

  const getPoint = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const source = event.touches ? event.touches[0] : event;

    return {
      x: source.clientX - rect.left,
      y: source.clientY - rect.top,
    };
  };

  const startDraw = (event) => {
    event.preventDefault();
    event.currentTarget?.setPointerCapture?.(event.pointerId);
    drawing.current = true;

    const context = canvasRef.current.getContext("2d");
    const point = getPoint(event);
    lastPoint.current = point;
    context.beginPath();
    context.moveTo(point.x, point.y);
  };

  const endDraw = (event) => {
    if (event) {
      event.preventDefault();
      event.currentTarget?.releasePointerCapture?.(event.pointerId);
    }
    drawing.current = false;
    lastPoint.current = null;
  };

  const draw = (event) => {
    if (!drawing.current) return;
    event.preventDefault();

    const context = canvasRef.current.getContext("2d");
    const point = getPoint(event);

    if (!lastPoint.current) {
      lastPoint.current = point;
      context.beginPath();
      context.moveTo(point.x, point.y);
    }

    context.lineTo(point.x, point.y);
    context.stroke();
    setHasInk(true);
    lastPoint.current = point;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    context.fillStyle = "#ffffff";
    context.clearRect(0, 0, sizeRef.current.width, sizeRef.current.height);
    context.fillRect(0, 0, sizeRef.current.width, sizeRef.current.height);
    lastPoint.current = null;
    setHasInk(false);
    onClear?.();
  };

  const save = () => {
    if (!hasInk) return;
    const canvas = canvasRef.current;
    onSave(canvas.toDataURL("image/png"));
  };

  return (
    <div className="signaturePad">
      <canvas
        ref={canvasRef}
        width={BASE_WIDTH}
        height={BASE_HEIGHT}
        onPointerDown={startDraw}
        onPointerUp={endDraw}
        onPointerCancel={endDraw}
        onPointerMove={draw}
      />

      <div className="signatureActions">
        <button type="button" onClick={clear}>
          {"L\u00f6schen"}
        </button>
        <button type="button" onClick={save} disabled={!hasInk}>
          Unterschrift speichern
        </button>
      </div>
    </div>
  );
}
